import {
  AdsOrderStatus,
  CancelReasonEnum,
  CancelReasonInfo,
  getCancelReasonInfo,
} from "@/interface/order";
import getHoursDiffFromNow from "@/utils/getHoursDiffFromNow";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { OrderPriceNegotiationStatus } from "@/biz/order";
import { getCollaborationCancellationFromOrder } from "@/biz/orderCancellationDetail";
import { getAdsOrderStatusLabel } from "@/biz/orderStatusCopy";

const OrderStatusComponent: React.FC<{
  data: any;
  isOrderDetail?: boolean;
  showAhaPicksQuoteStatus?: boolean;
  align?: "left" | "center";
  size?: "default" | "mobile-detail";
}> = ({
  data,
  isOrderDetail,
  showAhaPicksQuoteStatus = false,
  align = "center",
  size = "default",
}) => {
  const { t } = useTranslation();
  const { status } = data || {};
  const creatorCancellation = getCollaborationCancellationFromOrder(data);
  const creatorEndedCollaboration = Boolean(creatorCancellation);
  const getStatusStyle = (status: string) => {
    switch (status) {
      case AdsOrderStatus.CollaborationEstablished:
        if (
          data?.priceNegotiationStatus ===
          OrderPriceNegotiationStatus.UnderAdsReview
        ) {
          return { bg: "#FE9A00", text: "#fff" };
        }
        if (
          data?.priceNegotiationStatus ===
          OrderPriceNegotiationStatus.UnderCreatorReview
        ) {
          return { bg: "#FFFBEB", text: "#973C00" };
        }
        // 达人确认价格
        if (
          data?.priceNegotiationStatus ===
          OrderPriceNegotiationStatus.PendingAdsApproval
        ) {
          return { bg: "#F5F3FF", text: "#5D0EC0" };
        }
        return { bg: "#EFF6FF", text: "#193CB8" };
      case AdsOrderStatus.UnderScriptReview:
      case AdsOrderStatus.UnderContentReview:
        return { bg: "#F6339A", text: "#fff" };
      case AdsOrderStatus.PendingProductShipment:
        return { bg: "#FEFCE8", text: "#854D0E" };
      case AdsOrderStatus.CollaborationWithdrawn:
        return { bg: "#FAFAFA", text: "#404040", border: "#E5E5E5" };
      case AdsOrderStatus.UnderScriptProduction:
      case AdsOrderStatus.UnderContentProduction:
        return { bg: "#ECFEFF", text: "#005F78" };
      case AdsOrderStatus.AdsPendingPublish:
        return { bg: "#ECFDF5", text: "#006045" };
      case AdsOrderStatus.AdsPublished:
        return { bg: "#00BC7D", text: "#fff" };
      case AdsOrderStatus.CollaborationCancelled:
        return { bg: "#FAFAFA", text: "#404040", border: "#E5E5E5" };
      case AdsOrderStatus.PendingProductArrival:
      case AdsOrderStatus.PendingCreatorAddress:
        return { bg: "#F5F3FF", text: "#4C1D95" };
      case AdsOrderStatus.PendingProductionKickoff:
        return { bg: "#FFFBEB", text: "#973C00" };
      case AdsOrderStatus.PendingCreatorAccountInfo:
        return { bg: "#EFF6FF", text: "#193CB8" };
      case AdsOrderStatus.PendingAccountSetup:
        return { bg: "#F5F3FF", text: "#5D0EC0" };
      case AdsOrderStatus.PendingSelection:
        return { bg: "#FFF6ED", text: "#9B2911" };
      case AdsOrderStatus.QuoteRequested:
        return { bg: "#EFF6FF", text: "#193CB8" };
      default:
        return { bg: "#fafafa", text: "#86909C" };
    }
  };

  const { bg, text, border } = getStatusStyle(status);
  const statusText = creatorEndedCollaboration
    ? t("campaignDetail.collaborationCancellation.status.influencerEnded", {
        defaultValue: "Influencer ended the collab",
      })
    : showAhaPicksQuoteStatus && status === AdsOrderStatus.QuoteRequested
      ? data?.ahaPicksOffer
        ? t(
            "components.orderStatusComponent.openingOfferSentAndAwaitingReply",
            {
              defaultValue: "Opening offer sent & awaiting reply",
            },
          )
        : t("components.orderStatusComponent.requestedQuoteAndAwaitingReply", {
            defaultValue: "Requested quote & awaiting reply",
          })
      : getAdsOrderStatusLabel(status, data, t);

  const getTooltipText = () => {
    if (creatorEndedCollaboration) {
      return t("campaignDetail.collaborationCancellation.status.tooltip", {
        defaultValue: "The influencer chose to end the negotiation.",
      });
    }
    if (status === AdsOrderStatus.PendingAccountSetup) {
      return t("components.orderStatusComponent.pendingAccountSetupTooltip");
    }
    if (status === AdsOrderStatus.PendingCreatorAccountInfo) {
      return t(
        "components.orderStatusComponent.pendingCreatorAccountInfoTooltip",
      );
    }
    if (
      status === AdsOrderStatus.CollaborationEstablished &&
      data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderAdsReview
    ) {
      return t("components.orderStatusComponent.underAdsReviewTooltip");
    }
    if (
      status === AdsOrderStatus.CollaborationEstablished &&
      data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderCreatorReview
    ) {
      return t("components.orderStatusComponent.underCreatorReviewTooltip");
    }
    if (
      status === AdsOrderStatus.CollaborationEstablished &&
      data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.PendingAdsApproval
    ) {
      return t("components.orderStatusComponent.pendingAdsApprovalTooltip");
    }
    if (
      isOrderDetail &&
      status === AdsOrderStatus.QuoteRequested &&
      !!data?.ahaPicksOffer
    ) {
      return t("components.orderStatusComponent.openingOfferSentTooltip");
    }
    if (
      status === AdsOrderStatus.CollaborationCancelled ||
      status === AdsOrderStatus.CollaborationWithdrawn
    ) {
      const cancelReason = data?.cancelReason as CancelReasonEnum | undefined;
      return cancelReason && CancelReasonInfo[cancelReason]
        ? getCancelReasonInfo(cancelReason, t).comment
        : "";
    }
    return "";
  };

  // 延迟后根据不同状态显示不同的文案
  const getStatusText = () => {
    return (
      <Tooltip
        title={t("components.orderStatusComponent.behindScheduleTooltip")}
      >
        <div
          className="h-6 px-4 flex items-center justify-center text-xs font-medium  leading-none rounded-[999px] whitespace-nowrap"
          style={{ backgroundColor: "#FFF6ED", color: "#9B2911" }}
        >
          {t("components.orderStatusComponent.slightlyBehindSchedule")}
        </div>
      </Tooltip>
    );
  };

  // 如果 data 不存在，返回 null
  if (!data) {
    return null;
  }

  return (
    <div
      className={`flex flex-col justify-center gap-1 ${
        align === "left" ? "items-start" : "items-center"
      }`}
    >
      <div className="flex justify-center">
        {/* 有下一操作时间，且逾期满 7 天（168 小时）后显示 Behind schedule */}
        {data.creatorNextOperateDate &&
        getHoursDiffFromNow(data.creatorNextOperateDate) <= -(7 * 24) ? (
          getStatusText()
        ) : (
          <Tooltip title={getTooltipText()}>
            <div
              className={`flex items-center justify-center whitespace-nowrap rounded-[999px] font-medium ${
                size === "mobile-detail"
                  ? "h-6 px-2 text-sm leading-5"
                  : "h-[22px] px-[6px] text-[12px] leading-none md:h-6 md:px-4 md:text-xs"
              }`}
              style={{
                backgroundColor: bg,
                color: text,
                border:
                  creatorEndedCollaboration || border
                    ? `1px solid ${border || "#E5E5E5"}`
                    : undefined,
              }}
            >
              {statusText}
            </div>
          </Tooltip>
        )}
      </div>
      {status === AdsOrderStatus.QuoteRequested &&
        !isOrderDetail &&
        !showAhaPicksQuoteStatus && (
          <div className="justify-start text-[#79716B] text-xs font-normal font-['DM_Sans'] leading-4">
            {!!data?.ahaPicksOffer
              ? t("components.orderStatusComponent.openingOfferSent", {
                  defaultValue: "Your opening offer sent",
                })
              : t("components.orderStatusComponent.youveRequestedQuote", {
                  defaultValue: "You've requested quote",
                })}
          </div>
        )}
    </div>
  );
};

export default OrderStatusComponent;
